import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronRight, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { BusinessCard } from "@/components/BusinessCard";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const SORTS = [
  { v: "recommended", l: "Recommended" },
  { v: "top_rated", l: "Top Rated" },
  { v: "most_popular", l: "Most Popular" },
  { v: "newest", l: "Newest" },
];

export default function CategoryPage({ kind }) {
  const { city, category } = useParams();
  const navigate = useNavigate();
  const isService = kind === "service";
  const basePath = isService ? "/services" : "";
  const listEndpoint = isService ? "/services" : "/businesses";
  const catsEndpoint = isService ? "/service-categories" : "/categories";
  const noun = isService ? "services" : "businesses";

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ cityName: city, catName: category });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");
  const [filters, setFilters] = useState({ open_now: false, verified: false, featured: false, min_rating: 0, price: "" });

  useSeo(`Best ${meta.catName} in ${meta.cityName} | OyeBest`, `Find the best ${meta.catName} in ${meta.cityName}. Ratings, reviews, contact & directions on OyeBest.`);

  useEffect(() => {
    api.get(catsEndpoint).then((r) => {
      const c = r.data.find((x) => x.slug === category);
      if (c) setMeta((m) => ({ ...m, catName: c.name }));
    });
    api.get("/cities").then((r) => {
      const c = r.data.find((x) => x.slug === city);
      if (c) setMeta((m) => ({ ...m, cityName: c.name }));
    });
  }, [city, category, catsEndpoint]);

  useEffect(() => {
    setLoading(true);
    const params = { city, category, sort, ...filters };
    if (!params.price) delete params.price;
    api.get(listEndpoint, { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [city, category, sort, filters, listEndpoint]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4 flex-wrap" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-primary">Home</Link><ChevronRight className="w-3.5 h-3.5" />
        {isService && <><Link to="/services" className="hover:text-primary">Services</Link><ChevronRight className="w-3.5 h-3.5" /></>}
        <Link to={`${basePath}/${city}`} className="hover:text-primary">{meta.cityName}</Link><ChevronRight className="w-3.5 h-3.5" />
        <span className="text-foreground font-medium">{meta.catName}</span>
      </nav>

      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Best {meta.catName} in {meta.cityName}</h1>
      <p className="text-muted-foreground mt-1.5">{loading ? "Loading…" : `${items.length} ${noun} found`}</p>

      <div className="flex flex-wrap items-center gap-3 mt-6 mb-8 p-3 rounded-2xl bg-secondary/60 border border-border">
        <span className="flex items-center gap-1.5 text-sm font-semibold px-2"><SlidersHorizontal className="w-4 h-4" /> Filters</span>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-40 h-10 rounded-full bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(filters.min_rating)} onValueChange={(v) => setFilters((f) => ({ ...f, min_rating: Number(v) }))}>
          <SelectTrigger className="w-32 h-10 rounded-full bg-white" data-testid="rating-select"><SelectValue placeholder="Rating" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any Rating</SelectItem>
            <SelectItem value="3">3★ & up</SelectItem>
            <SelectItem value="4">4★ & up</SelectItem>
            <SelectItem value="4.5">4.5★ & up</SelectItem>
          </SelectContent>
        </Select>
        {[["open_now", "Open Now"], ["verified", "Verified"], ["featured", "Featured"]].map(([k, l]) => (
          <div key={k} className="flex items-center gap-2 bg-white rounded-full px-3 h-10 border border-border">
            <Switch id={k} checked={filters[k]} onCheckedChange={(v) => setFilters((f) => ({ ...f, [k]: v }))} data-testid={`filter-${k}`} />
            <Label htmlFor={k} className="text-sm font-medium cursor-pointer">{l}</Label>
          </div>
        ))}
      </div>

      {loading ? <CardsSkeleton /> : items.length === 0 ? (
        <div className="text-center py-20 rounded-2xl bg-secondary/50 border border-border">
          <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-display text-xl font-bold">Can't find what you're looking for?</h3>
          <p className="text-muted-foreground mt-1">No {noun} match your filters yet.</p>
          <Button className="mt-5 rounded-full" onClick={() => navigate(isService ? "/list-service" : "/list-business")} data-testid="add-business-btn">{isService ? "List Your Service" : "Add This Business"}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
        </div>
      )}
    </div>
  );
}
